import { API_URL } from '../config/config';
import { getRequest } from '../utils/axiosUtils';
import buildQueryParams from '../utils/buildQueryParams';

/**
 * Member Messaging Activity report data.
 *
 * getMessagingActivity → the list page: one row per non-bot member with an
 * ALL-TIME messaging snapshot (people messaged / messaged-her, sent / received,
 * reply rate, likes) computed server-side. `joined` is the only date control and
 * scopes which members appear, by signup date. Content is never returned — counts only.
 *
 * getMemberMessageHistory → the per-member detail page: accurate per-period
 * metric blocks, a 13-month volume series, and the member's conversation list.
 *
 * The v6 report frames messaging by replies on both sides and adds fields the
 * backend must return — see API_CHANGES.md in the container folder for the full
 * contract (snap.orep/irep/outMin/inMin, member.account, population reply-split
 * `totals`, month.repliedOut, period.orep, the `6mo` period, and `data.countries`).
 * Missing fields degrade to "—" client-side, so shipping them is additive.
 */
export const getMessagingActivity = async (filter = {}) => {
  const { country, state, city, gender, searchTerm, joined, from, to, limit, sort, page, verified, account } = filter;

  const qp = buildQueryParams({ country, state, city, gender, searchTerm, limit });
  const extra = [];
  // `joined` scopes which members are listed, by signup date; with joined=custom it
  // carries an inclusive from/to (YYYY-MM-DD, read as IST days server-side).
  if (joined) extra.push(`joined=${encodeURIComponent(joined)}`);
  if (from) extra.push(`from=${encodeURIComponent(from)}`);
  if (to) extra.push(`to=${encodeURIComponent(to)}`);
  if (sort) extra.push(`sort=${encodeURIComponent(sort)}`);
  if (page) extra.push(`page=${encodeURIComponent(page)}`);
  if (verified) extra.push('verified=true');
  // Account status filter: approved | hidden | inactive | deactivated | suspended | deleted,
  // or active = the Approved + Hidden total (utils/memberStatus.js).
  if (account) extra.push(`account=${encodeURIComponent(account)}`);
  const extraStr = extra.join('&');
  const query = [qp, extraStr].filter(Boolean).join('&');

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/getMessagingActivity${query ? `?${query}` : ''}`,
    isAbort: false,
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};

// `period` windows the CONVERSATION LIST only (all | 7d | 1mo | 3mo | 6mo | 12mo);
// the metric blocks in `periods` are always all returned, so the page switches
// cards and charts without a round trip.
export const getMemberMessageHistory = async (memberId, period) => {
  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/getMemberMessageHistory/${memberId}${period ? `?period=${encodeURIComponent(period)}` : ''}`,
    isAbort: false,
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};

/**
 * Just the conversation panel, for when the period selector moves. Refetching the
 * whole history payload would recompute five metric windows and a 13-month series to
 * change one list — and flash the full-page loader on every click of the period strip.
 */
export const getMemberConversations = async (memberId, period) => {
  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/getMemberConversations/${memberId}${period ? `?period=${encodeURIComponent(period)}` : ''}`,
    isAbort: false,
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};

/**
 * One conversation's transcript — the member and a single partner, over the same
 * period the conversation list is windowed to.
 *
 * Fetched lazily, when an admin expands that row: the list itself carries up to 80
 * conversations, and shipping every message of all of them to answer "what was said
 * in this one" would be neither fast nor readable.
 */
export const getMemberConversationThread = async (memberId, partnerId, period) => {
  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/getMemberConversationThread/${memberId}/${partnerId}${period ? `?period=${encodeURIComponent(period)}` : ''}`,
    isAbort: false,
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};
